import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CapaCreateDialog } from "@/components/quality/CapaCreateDialog";
import { apiErrorMessage } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { ClipboardList, Search, Plus } from "lucide-react";

export default function Capa() {
  const { pathname } = useLocation();
  const isNC = pathname.startsWith("/nonconformity");
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useResource("/capa");
  const rows = useMemo(() => {
    const all = Array.isArray(data) ? data : [];
    return isNC ? all.filter((c) => String(c.type || "").toUpperCase() === "NC") : all;
  }, [data, isNC]);

  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = rows.filter((c) => !q || `${c.title} ${c.capaId || ""} ${c.owner || ""}`.toLowerCase().includes(q.toLowerCase()));
  const afterCreate = () => { qc.invalidateQueries({ queryKey: ["resource", "/capa"] }); refetch(); };

  return (
    <div className="space-y-6">
      <PageHeader title={isNC ? "Non-Conformity" : "CAPA"} subtitle={isNC ? "Non-conformities raised from audits & checks." : "Corrective & Preventive Actions — problem, root cause, action, owner, due date and closure."}>
        <Button data-testid="capa-new" className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> New {isNC ? "NC" : "CAPA"}
        </Button>
      </PageHeader>

      <div className="flex justify-end">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input data-testid="capa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / owner…" className="h-9 pl-8 text-sm" />
        </div>
      </div>

      {isLoading ? <LoadingState label="Loading…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        filtered.length === 0 ? <EmptyState title={isNC ? "No non-conformities" : "No CAPA records"} message="Records created on mobile or web appear here." /> : (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-4 py-2.5">Ref</th><th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Severity</th>
                  <th className="px-4 py-2.5">Owner</th><th className="px-4 py-2.5">Due</th><th className="px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`capa-row-${c.id}`} className="cursor-pointer hover:bg-amber-50/40" onClick={() => setSelected(c)}>
                    <td className="px-4 py-2.5 font-mono text-xs text-stone-600"><span className="flex items-center gap-2"><ClipboardList className="h-3.5 w-3.5 text-amber-700" />{c.capaId || c.ncId || (c.id || "").slice(0, 8)}</span></td>
                    <td className="px-4 py-2.5 text-stone-800">{c.title}</td>
                    <td className="px-4 py-2.5"><StatusBadge value={c.severity} /></td>
                    <td className="px-4 py-2.5 text-stone-600">{c.owner || c.assignedTo || "—"}</td>
                    <td className="px-4 py-2.5">{fmtDate(c.dueDate)}</td>
                    <td className="px-4 py-2.5"><StatusBadge value={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-lg">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 font-display">{selected.title} <StatusBadge value={selected.status} /></SheetTitle>
                <SheetDescription className="text-xs">{selected.capaId || selected.id} · {fmtDateTime(selected.createdAt)}</SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-3 text-sm">
                {selected.linkedAuditId && <Field label="Source audit" value={`${selected.linkedAuditType || ""} · ${selected.findingRef || selected.linkedAuditId}`} />}
                <Field label="Severity" value={selected.severity} />
                <Field label="Category / area" value={selected.category} />
                <Field label="Finding / description" value={selected.description} block />
                <Field label="Root cause" value={selected.rootCause} block />
                <Field label="Corrective action" value={selected.correctiveAction} block />
                <Field label="Preventive action" value={selected.preventiveAction} block />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Responsible" value={selected.owner || selected.assignedTo} />
                  <Field label="Target date" value={fmtDate(selected.dueDate)} />
                  <Field label="Closure date" value={fmtDate(selected.closedAt || selected.closureDate)} />
                  <Field label="Evidence" value={Array.isArray(selected.photos) ? `${selected.photos.length} photo(s)` : "—"} />
                </div>
                {selected.remarks && <Field label="Completion notes" value={selected.remarks} block />}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CapaCreateDialog open={createOpen} onOpenChange={setCreateOpen} isNC={isNC} onCreated={afterCreate} />
    </div>
  );
}

function Field({ label, value, block }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-stone-400">{label}</div>
      <div className={`text-stone-800 ${block ? "whitespace-pre-wrap text-sm" : ""}`}>{value || "—"}</div>
    </div>
  );
}
